/**
 * 用户状态管理
 * 处理用户登录、登出、权限等相关状态
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { permissionManager } from '@/auth'
import type { UserInfo as AuthUserInfo } from '@/auth/types'
import {
  login as loginApi,
  logout as logoutApi,
  getCurrentUserInfo as getCurrentUserInfoApi,
  getCurrentUserPermissions as getCurrentUserPermissionsApi,
  refreshAccessToken as refreshAccessTokenApi,
  type IOldLoginResponse,
  type IUserInfoResponse,
  type ILoginParams
} from '@/modules/auth/api/api'
import type { UserPermission } from '@/types/navigation'
import { isNewApiEnabled } from '@/utils/request'

// 用户信息接口
export interface UserInfo extends AuthUserInfo {
  name: string
  roles: string[]
  permissions: string[]
  fullPermissions?: UserPermission[]
  tenantId?: string
  tenantName?: string
}

// 登录参数接口（扩展API接口，添加业务字段）
export interface LoginParams extends ILoginParams {
  rememberMe?: boolean
}

// 登录结果接口
export interface LoginResult {
  success: boolean
  message?: string
  needCaptcha?: boolean
  token?: string
  refreshToken?: string
  userInfo?: UserInfo
}

export const useUserStore = defineStore('user', () => {
  // 响应式状态
  const token = ref<string>('')
  const refreshToken = ref<string>('')
  const userInfo = ref<UserInfo | null>(null)
  const permissions = ref<string[]>([])
  const roles = ref<string[]>([])
  const isLoading = ref(false)
  const fullPermissions = ref<UserPermission[]>([]) // 完整权限数据，用于导航配置

  // 计算属性
  const isLoggedIn = computed(() => !!token.value && !!userInfo.value)
  const userName = computed(() => userInfo.value?.name || userInfo.value?.username || '')
  const userAvatar = computed(() => userInfo.value?.avatar || '')
  const userRoles = computed(() => userInfo.value?.roles || [])
  const userPermissions = computed(() => userInfo.value?.permissions || [])
  const isAdmin = computed(() => {
    const userRolesList = userInfo.value?.roles || []
    return userRolesList.includes('admin') || userRolesList.includes('super_admin')
  })

  const buildUserInfo = (source: IUserInfoResponse, permissionCodes: string[]): UserInfo => {
    const nowIso = new Date().toISOString()
    const sourceRoles = Array.isArray(source.roles) ? source.roles : []
    const derivedRoles = source.isAdmin ? ['admin'] : (source.isTenantAdmin ? ['tenant_admin'] : [])
    const mergedRoles = Array.from(new Set([...sourceRoles, ...derivedRoles]))
    const mergedPermissions = source.isAdmin
      ? Array.from(new Set([...permissionCodes, '*']))
      : permissionCodes
    // 从权限代码构建完整权限对象，避免 fullPermissions 为空
    const fullPermissions = mergedPermissions.map(code => ({
      code,
      name: code,
      type: 'menu' as const,
      description: ''
    }))
    return {
      id: String(source.id ?? ''),
      username: source.username || '',
      name: source.realName || source.username || '',
      email: source.email || '',
      phone: source.phone || '',
      avatar: source.avatar || '',
      nickname: source.realName || source.username || '',
      status: 'active',
      emailVerified: false,
      phoneVerified: false,
      createdAt: nowIso,
      updatedAt: nowIso,
      roles: mergedRoles,
      permissions: mergedPermissions,
      fullPermissions,
      tenantId: source.tenantId !== undefined ? String(source.tenantId) : undefined,
      tenantName: source.accountType || '',
      lastLoginAt: source.lastLoginTime || nowIso
    }
  }

  // 登录方法
  const login = async (params: LoginParams): Promise<LoginResult> => {
    try {
      isLoading.value = true

      const response = await loginApi({
        username: params.username,
        password: params.password
      })

      let loginToken = ''
      const nextRefreshToken = ''
      let nextUserInfo: UserInfo | null = null
      let userPermissions: string[] = []

      if (isNewApiEnabled('auth')) {
        loginToken = response.token
        const user = response.userInfo
        const basePermissions = Array.isArray(user.permissions) ? user.permissions : []
        userPermissions = user.isAdmin ? Array.from(new Set([...basePermissions, '*'])) : basePermissions
        nextUserInfo = buildUserInfo(user, userPermissions)
        fullPermissions.value = nextUserInfo.fullPermissions
      } else {
        const legacy = response as unknown as IOldLoginResponse
        const legacyRoles = legacy.roles.map(role => role.name)
        userPermissions = (legacy.permInfo.permCodes && legacy.permInfo.permCodes.length > 0)
          ? [...legacy.permInfo.permCodes]
          : (legacy.permInfo.perms?.map(perm => perm.code).filter((code): code is string => Boolean(code)) || [])
        if (legacy.isSuperAdmin === 1) {
          userPermissions.push('*')
        }
        const nowIso = new Date().toISOString()
        const authStatus: AuthUserInfo['status'] = legacy.status === 1 ? 'active' : 'inactive'
        nextUserInfo = {
          id: legacy.id.toString(),
          username: legacy.username,
          name: legacy.nickname || legacy.username,
          email: legacy.email || '',
          phone: legacy.phone || '',
          avatar: '',
          nickname: legacy.nickname || legacy.username,
          status: authStatus,
          emailVerified: false,
          phoneVerified: false,
          createdAt: nowIso,
          updatedAt: nowIso,
          roles: legacyRoles,
          permissions: userPermissions,
          fullPermissions: legacy.permInfo.perms || [],
          tenantId: 'default',
          tenantName: '默认租户',
          lastLoginAt: legacy.lastLoginAt || nowIso
        }
        fullPermissions.value = legacy.permInfo.perms || []
        loginToken = legacy.tokenValue
      }

      const result: LoginResult = {
        success: true,
        token: loginToken,
        refreshToken: nextRefreshToken,
        userInfo: nextUserInfo || undefined
      }

      console.log('✅ 登录成功，用户信息:', nextUserInfo)
      console.log('✅ 用户权限列表:', userPermissions)

      return result
    } catch (error: any) {
      console.error('❌ 登录失败:', error)
      return {
        success: false,
        message: error.message || '登录失败，请稍后重试'
      }
    } finally {
      isLoading.value = false
    }
  }

  // 处理登录成功后的状态更新
  const handleLoginSuccess = async (result: LoginResult, rememberMe = false) => {
    if (result.success && result.token && result.userInfo) {
      // 保存登录信息
      token.value = result.token
      refreshToken.value = result.refreshToken || ''
      userInfo.value = result.userInfo
      permissions.value = result.userInfo.permissions
      roles.value = result.userInfo.roles

      // 保存到本地存储
      if (rememberMe) {
        localStorage.setItem('token', result.token)
        localStorage.setItem('auth_token', result.token)
        localStorage.setItem('refreshToken', result.refreshToken || '')
        localStorage.setItem('auth_refresh_token', result.refreshToken || '')
        localStorage.setItem('userInfo', JSON.stringify(result.userInfo))
      } else {
        sessionStorage.setItem('token', result.token)
        sessionStorage.setItem('auth_token', result.token)
        sessionStorage.setItem('refreshToken', result.refreshToken || '')
        sessionStorage.setItem('auth_refresh_token', result.refreshToken || '')
        sessionStorage.setItem('userInfo', JSON.stringify(result.userInfo))
      }

      // 同步权限数据到权限管理器
      permissionManager.initialize({
        user: result.userInfo,
        token: result.token,
        permissions: result.userInfo.permissions,
        roles: result.userInfo.roles,
        isAuthenticated: true,
        loading: false,
        error: null
      })

      const { usePermissionStore } = await import('@/stores/permission')
      const permissionStore = usePermissionStore()
      const accessRoutes = await permissionStore.generateRoutes()
      const { addDynamicRoutes } = await import('@/router/permission')
      const router = (await import('@/router')).default
      addDynamicRoutes(router, accessRoutes)

      // 清除登录过期标记，避免路由守卫将已登录用户再次视为未登录
      try {
        localStorage.removeItem('auth_expired')
        sessionStorage.removeItem('auth_expired')
      } catch (error) {
        console.warn('清理登录过期标记失败:', error)
      }

      // 初始化导航配置
      try {
        const { useNavigationStore } = await import('@/stores/navigation')
        const navigationStore = useNavigationStore()
        await navigationStore.initNavigation()
        console.log('导航配置初始化完成')
      } catch (navError) {
        console.warn('导航配置初始化失败:', navError)
      }

      console.log('✅ 登录成功，权限已同步到权限管理器，权限列表:', result.userInfo.permissions)
    }
  }

  // 更新后的登录方法调用
  const performLogin = async (params: LoginParams): Promise<LoginResult> => {
    const result = await login(params)
    if (result.success) {
      await handleLoginSuccess(result, params.rememberMe)
    }
    return result
  }

  const mobileLogin = async (): Promise<LoginResult> => {
    return {
      success: false,
      message: '当前版本不支持短信验证码登录'
    }
  }

  /**
   * 用户退出登录
   * 统一的退出登录逻辑，包含API调用和本地数据清理
   * @param showMessage 是否显示退出消息，默认为true
   */
  const logout = async (showMessage = true, skipApi = false) => {
    try {
      if (!skipApi) {
        await logoutApi()
      }

      // 清理状态
      token.value = ''
      refreshToken.value = ''
      userInfo.value = null
      permissions.value = []
      roles.value = []
      fullPermissions.value = []

      // 清除本地存储
      localStorage.removeItem('token')
      localStorage.removeItem('auth_token')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('auth_refresh_token')
      localStorage.removeItem('userInfo')
      sessionStorage.removeItem('token')
      sessionStorage.removeItem('auth_token')
      sessionStorage.removeItem('refreshToken')
      sessionStorage.removeItem('auth_refresh_token')
      sessionStorage.removeItem('userInfo')

      // 清除认证管理器的数据
      permissionManager.clear()

      // 清理导航配置
      try {
        const { useNavigationStore } = await import('@/stores/navigation')
        const navigationStore = useNavigationStore()
        // 使用内部方法重置导航状态
        await navigationStore.refreshNavigation()
        console.log('导航配置已清理')
      } catch (navError) {
        console.warn('导航配置清理失败:', navError)
      }

      if (showMessage) {
        ElMessage.success('已安全退出')
      }
    } catch (error: any) {
      console.error('登出失败:', error)

      // 即使API调用失败，也要清除本地数据，确保用户能够退出
      token.value = ''
      refreshToken.value = ''
      userInfo.value = null
      permissions.value = []
      roles.value = []

      localStorage.removeItem('token')
      localStorage.removeItem('auth_token')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('auth_refresh_token')
      localStorage.removeItem('userInfo')
      sessionStorage.removeItem('token')
      sessionStorage.removeItem('auth_token')
      sessionStorage.removeItem('refreshToken')
      sessionStorage.removeItem('auth_refresh_token')
      sessionStorage.removeItem('userInfo')

      permissionManager.clear()

      // 清理导航配置
      try {
        const { useNavigationStore } = await import('@/stores/navigation')
        const navigationStore = useNavigationStore()
        // 使用内部方法重置导航状态
        await navigationStore.refreshNavigation()
        console.log('导航配置已清理')
      } catch (navError) {
        console.warn('导航配置清理失败:', navError)
      }

      if (showMessage) {
        ElMessage.warning('退出登录时网络异常，但已清除本地登录状态')
      }
    }
  }

  // 刷新token
  const refreshTokenMethod = async (): Promise<boolean> => {
    try {
      if (!isNewApiEnabled('auth')) {
        return false
      }
      const result = await refreshAccessTokenApi()
      token.value = result.token
      refreshToken.value = ''

      // 更新本地存储
      const storage = localStorage.getItem('token') || localStorage.getItem('auth_token') ? localStorage : sessionStorage
      storage.setItem('token', result.token)
      storage.setItem('auth_token', result.token)

      return true
    } catch (error) {
      console.error('刷新token失败:', error)
      await logout(false)
      return false
    }
  }

  // 获取用户信息
  const getUserInfo = async (): Promise<UserInfo | null> => {
    try {
      if (!token.value) {
        return null
      }

      // 如果已有用户信息，直接返回
      if (userInfo.value) {
        return userInfo.value
      }
      if (!isNewApiEnabled('auth')) {
        return null
      }
      const source = await getCurrentUserInfoApi()
      const permissionList = await getCurrentUserPermissionsApi()
      const info = buildUserInfo(source, permissionList || source.permissions || [])
      userInfo.value = info
      permissions.value = info.permissions
      roles.value = info.roles

      // 保存用户信息到本地存储
      const userInfoStr = JSON.stringify(info)
      localStorage.setItem('userInfo', userInfoStr)
      sessionStorage.setItem('userInfo', userInfoStr)

      console.log('✅ 用户信息已保存到本地存储:', {
        userId: info.id,
        username: info.username,
        permissions: info.permissions.length,
        roles: info.roles.length
      })

      return info
    } catch (error) {
      console.error('获取用户信息失败:', error)
      return null
    }
  }

  // 检查权限
  const hasPermission = (permission: string): boolean => {
    return permissions.value.includes(permission)
  }

  // 检查角色
  const hasRole = (role: string): boolean => {
    return roles.value.includes(role)
  }

  // 检查多个权限（AND关系）
  const hasAllPermissions = (permissionList: string[]): boolean => {
    return permissionList.every(permission => hasPermission(permission))
  }

  // 检查多个权限（OR关系）
  const hasAnyPermission = (permissionList: string[]): boolean => {
    return permissionList.some(permission => hasPermission(permission))
  }

  // 初始化用户状态（从本地存储恢复）
  const initUserState = async () => {
    console.log('🔄 开始初始化用户状态...')

    const storageToken = localStorage.getItem('token')
      || localStorage.getItem('auth_token')
      || sessionStorage.getItem('token')
      || sessionStorage.getItem('auth_token')
    const storageRefreshToken = localStorage.getItem('refreshToken')
      || localStorage.getItem('auth_refresh_token')
      || sessionStorage.getItem('refreshToken')
      || sessionStorage.getItem('auth_refresh_token')
    const storageUserInfo = localStorage.getItem('userInfo') || sessionStorage.getItem('userInfo')

    if (storageToken) {
      console.log('  - Token值:', `${storageToken.substring(0, 20)}...`)
    }

    // 如果有token，先恢复token状态
    if (storageToken) {
      token.value = storageToken
      refreshToken.value = storageRefreshToken || ''
      console.log('✅ Token状态已恢复')

      // 如果同时有用户信息，则恢复用户信息
      if (storageUserInfo) {
        try {
          const parsedUserInfo = JSON.parse(storageUserInfo)
          userInfo.value = parsedUserInfo
          permissions.value = parsedUserInfo?.permissions || []
          roles.value = parsedUserInfo?.roles || []
          // 恢复完整权限数据
          fullPermissions.value = parsedUserInfo?.fullPermissions || []

          // 同步权限数据到权限管理器
          permissionManager.initialize({
            user: parsedUserInfo,
            token: storageToken,
            permissions: parsedUserInfo?.permissions || [],
            roles: parsedUserInfo?.roles || [],
            isAuthenticated: true,
            loading: false,
            error: null
          })

          console.log('✅ 用户信息和权限已恢复，权限列表:', parsedUserInfo?.permissions)

          // 初始化导航配置
          try {
            const { useNavigationStore } = await import('@/stores/navigation')
            const navigationStore = useNavigationStore()
            await navigationStore.initNavigation()
            console.log('✅ 导航配置初始化完成（页面刷新恢复）')
          } catch (navError) {
            console.warn('⚠️ 导航配置初始化失败:', navError)
          }

        } catch (error) {
          console.error('❌ 恢复用户信息失败:', error)
          // 清除损坏的用户信息，但保留token
          localStorage.removeItem('userInfo')
          sessionStorage.removeItem('userInfo')
        }
      } else {
        console.log('⚠️ 有Token但没有用户信息，需要重新获取')
      }

      // 设置认证管理器的认证数据
      // 注意：authManager没有setToken方法，暂时注释掉
      // authManager.setToken(storageToken)
    } else {
      console.log('❌ 没有找到Token，用户未登录')
    }

  }

  /**
   * 刷新用户权限缓存
   * 重新从服务器获取最新的权限数据
   */
  const refreshPermissions = async (): Promise<void> => {
    try {
      console.log('🔄 开始刷新用户权限缓存...')

      if (!userInfo.value?.id) {
        console.warn('⚠️ 用户信息不存在，无法刷新权限')
        return
      }

      // 调用API获取最新的用户权限信息
      const { getUserPermissions } = await import('@/modules/system/api/menus')
      const latestPermissions = await getUserPermissions(parseInt(userInfo.value.id))

      // 更新权限缓存
      if (latestPermissions?.perms) {
        fullPermissions.value = latestPermissions.perms

        // 更新简化的权限代码列表
        const permissionCodes = latestPermissions.permCodes
        permissions.value = permissionCodes

        // 更新用户信息中的权限
        if (userInfo.value) {
          userInfo.value.permissions = permissionCodes
          userInfo.value.fullPermissions = latestPermissions.perms
        }

        // 同步到本地存储
        const storage = localStorage.getItem('rememberMe') === 'true' ? localStorage : sessionStorage
        if (userInfo.value) {
          storage.setItem('userInfo', JSON.stringify(userInfo.value))
        }

        console.log('✅ 权限缓存刷新成功，最新权限数量:', permissionCodes.length)
      } else {
        console.warn('⚠️ 获取到的权限数据格式异常')
      }

    } catch (error) {
      console.error('❌ 刷新权限缓存失败:', error)
      throw error
    }
  }

  return {
    // 状态
    token,
    refreshToken,
    userInfo,
    permissions,
    roles,
    isLoading,
    fullPermissions,

    // 计算属性
    isLoggedIn,
    userName,
    userAvatar,
    userRoles,
    userPermissions,
    isAdmin,

    // 方法
    login,
    performLogin,
    mobileLogin,
    logout,
    refreshTokenMethod,
    getUserInfo,
    hasPermission,
    hasRole,
    hasAllPermissions,
    hasAnyPermission,
    initUserState,
    refreshPermissions // 新增的刷新权限方法
  }
})
